import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from "@angular/core";
import { FmsService } from "src/app/services/fms.service";
import { RadioButton, RadioButtonsModule } from "@storeblocks/radio-buttons-ng";
import { SkeletonModule } from "@storeblocks/skeleton-ng";
import { getIsNotNullable, Nullable, stringToBoolean } from "src/app/utils/utils";
import { ConsentLabels } from "src/app/modules/shared/components/onboarding/onboarding-public-pension-step/onboarding-public-pension-step-radio-creator";
import { FormControl, Validators } from "@angular/forms";

import { Router } from "@angular/router";
import { UserConsentsService } from "src/app/services/user-consents.service";
import { AccordionModule } from "@storeblocks/accordion-ng";
import { FmsPipe } from "src/app/modules/shared/pipes/fms.pipe";
import { FormatTextForConsentPipe } from "src/app/modules/shared/pipes/format-text-for-consent.pipe";
import { ButtonModule } from "@storeblocks/button-ng";
import { ModalModule } from "@storeblocks/modal-ng";
import { DialogRef } from "src/app/services/dialog/dialog-ref";
import { DIALOG_DATA } from "src/app/services/dialog/dialog.service";

enum CONSENT_KEYS {
  GROUP,
  MARKETING,
}
interface DialogData {
  url: string;
  queryParams?: Record<string, any>;
}

@Component({
  selector: "app-onboarding-group-and-marketing-consents",
  templateUrl: "./group-and-marketing-consents.component.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RadioButtonsModule,
    AccordionModule,
    FmsPipe,
    FormatTextForConsentPipe,
    ButtonModule,
    ModalModule,
    SkeletonModule,
  ],
})
export class GroupAndMarketingConsentsComponent {
  private readonly dialogRef = inject<DialogRef<GroupAndMarketingConsentsComponent>>(DialogRef);
  private readonly fmsService = inject(FmsService);
  private readonly router = inject(Router);
  private readonly userConsentsService = inject(UserConsentsService);

  private readonly consentLabels = {
    yesLabel: this.fmsService.getText("onboardingConsents.yes"),
    noLabel: this.fmsService.getText("onboardingConsents.no"),
  };

  /** True once GET /user/consents has answered, with the consents or with an error. */
  private readonly isLoaded = computed(
    (): boolean =>
      this.userConsentsService.consents.hasValue() || this.userConsentsService.consents.status() === "error",
  );

  private isDestroyed = false;

  readonly data = inject<DialogData>(DIALOG_DATA);

  public steps = [
    {
      step: CONSENT_KEYS.GROUP,
      fmsKey: "consents.button.continue",
    },
    {
      step: CONSENT_KEYS.MARKETING,
      fmsKey: "consents.button.saveAndNext",
    },
  ];
  public readonly step = signal(CONSENT_KEYS.GROUP);
  public consentKeys = CONSENT_KEYS;

  /** False until the stored answers are pre-filled, so a skeleton shows instead of the radio buttons. */
  public readonly isPrefilled = signal(false);

  public readonly groupRadioButtons = signal<Array<RadioButton>>([]);
  public readonly marketingElectronicRadioButtons = signal<Array<RadioButton>>([]);
  public readonly marketingSocialMediaRadioButtons = signal<Array<RadioButton>>([]);
  public groupConsentControl: FormControl = new FormControl(null, Validators.required);
  public marketingElectronicConsentControl: FormControl = new FormControl(null, Validators.required);
  public marketingSocialMediaConsentControl: FormControl = new FormControl(null, Validators.required);
  public readonly isSubmitting = signal(false);

  constructor() {
    this.userConsentsService.load();

    // A save that ends after the dialog was closed must not close it again or navigate.
    inject(DestroyRef).onDestroy(() => (this.isDestroyed = true));

    // Pre-fills once with the stored answers. A failed GET selects nothing, like answers never given.
    effect(() => {
      if (!this.isLoaded() || untracked(this.isPrefilled)) {
        return;
      }

      const consents = this.userConsentsService.consents.hasValue()
        ? this.userConsentsService.consents.value()
        : undefined;
      this.createGroupRadioButtons(consents?.hasGroupConsent ?? undefined);
      this.marketingElectronicConsentControl.setValue(consents?.hasElectronicSolicitationConsent ?? undefined);
      this.marketingSocialMediaConsentControl.setValue(consents?.hasSocialMediaSolicitationConsent ?? undefined);
      this.isPrefilled.set(true);
    });
  }

  public goNext(): void {
    if (this.step() === CONSENT_KEYS.GROUP) {
      if (this.groupConsentControl.invalid) {
        this.groupConsentControl.markAsTouched();
        return;
      }
      this.step.set(CONSENT_KEYS.MARKETING);
      this.createMarketingRadioButtons(
        this.marketingElectronicConsentControl.value,
        this.marketingSocialMediaConsentControl.value,
      );
    } else if (this.step() === CONSENT_KEYS.MARKETING) {
      if (this.marketingElectronicConsentControl.invalid) {
        this.marketingElectronicConsentControl.markAsTouched();
      }
      if (this.marketingSocialMediaConsentControl.invalid) {
        this.marketingSocialMediaConsentControl.markAsTouched();
      }

      if (this.marketingElectronicConsentControl.invalid || this.marketingSocialMediaConsentControl.invalid) {
        return;
      }

      void this.saveAndNavigate();
    }
  }

  public goBack(): void {
    this.step.set(CONSENT_KEYS.GROUP);
    this.createGroupRadioButtons(this.groupConsentControl.value);
  }

  public setControlValue(formControl: FormControl, item: RadioButton): void {
    const booleanValue = stringToBoolean(item.value);
    formControl.markAsTouched();
    formControl.setValue(booleanValue);
  }

  public close(): void {
    this.dialogRef.close();
  }

  /**
   * Saves the three answers, then closes the dialog and navigates. A failure keeps the dialog open, without a
   * toast. In advisor incognito mode the save sends nothing and resolves null, so the dialog closes and navigates,
   * as before. When the dialog was closed during the save, nothing more happens, as when the old subscription
   * ended with the dialog.
   */
  private async saveAndNavigate(): Promise<void> {
    this.isSubmitting.set(true);

    const isSaved = await this.userConsentsService
      .save({
        hasGroupConsent: this.groupConsentControl.value,
        hasElectronicSolicitationConsent: this.marketingElectronicConsentControl.value,
        hasSocialMediaSolicitationConsent: this.marketingSocialMediaConsentControl.value,
      })
      .then(
        () => true,
        () => false,
      );

    if (this.isDestroyed) {
      return;
    }

    if (!isSaved) {
      this.isSubmitting.set(false);
      return;
    }

    this.dialogRef.close();
    this.isSubmitting.set(false);
    this.router.navigate([this.data.url], {
      queryParams: this.data.queryParams,
      queryParamsHandling: this.data.queryParams ? "merge" : undefined,
    });
  }

  private createGroupRadioButtons(checked: boolean | undefined = undefined): void {
    this.groupRadioButtons.set(this.createConsentRadioButtons(this.consentLabels, checked));
    this.groupConsentControl.setValue(checked);
  }

  private createMarketingRadioButtons(
    electronicChecked: boolean | undefined = undefined,
    socialMediaChecked: boolean | undefined = undefined,
  ): void {
    this.marketingElectronicRadioButtons.set(this.createConsentRadioButtons(this.consentLabels, electronicChecked));
    this.marketingElectronicConsentControl.setValue(electronicChecked);
    this.marketingSocialMediaRadioButtons.set(this.createConsentRadioButtons(this.consentLabels, socialMediaChecked));
    this.marketingSocialMediaConsentControl.setValue(socialMediaChecked);
  }

  private createConsentRadioButtons(
    labels: ConsentLabels,
    consentValue: Nullable<boolean> = undefined,
  ): Array<RadioButton> {
    const hasConsentValue = getIsNotNullable(consentValue);

    return [
      {
        label: labels.yesLabel,
        value: true.toString(),
        disabled: false,
        checked: hasConsentValue ? consentValue : false,
      },
      {
        label: labels.noLabel,
        value: false.toString(),
        disabled: false,
        checked: hasConsentValue ? !consentValue : false,
      },
    ];
  }
}
